import { defineMessage, useIntl } from 'react-intl';

import { renderDateString, useDateLocale } from '../../common/utils/utils';
import type { DatePickerValue } from '../../types';

const noDateSetMessage = defineMessage({
	id: 'date-range-picker.date-range.no-date-set',
	defaultMessage: 'No target date set',
	description: 'Message to show when no target date is set',
});

const fiscalYearPrefixMessage = defineMessage({
	id: 'date-range-picker.date-range.fiscal-year-prefix',
	defaultMessage: 'FY',
	description: 'Fiscal year prefix used in interval labels (e.g. "Q1 FY26")',
});

export type UseDateLabelOptions = {
	value: DatePickerValue | undefined;
	placeholder?: string;
	withTimeScale?: boolean;
	showPlaceholderWithSubtleText?: boolean;
	/** When set and value is set, shown instead of the formatted date range (e.g. interval name). */
	intervalDisplayName?: string;
};

/**
 * Shared hook for computing the date label and text color for DateRange components.
 * Used by both DateRange and ViewOnlyDateRange to maintain consistency.
 */
export const useDateLabel = ({
	value: date,
	placeholder,
	withTimeScale,
	intervalDisplayName,
}: UseDateLabelOptions): string => {
	const intl = useIntl();
	const locale = useDateLocale();

	const noDateSelectedMessage = placeholder ? placeholder : intl.formatMessage(noDateSetMessage);

	const fiscalYearPrefix =
		intervalDisplayName !== undefined ? intl.formatMessage(fiscalYearPrefixMessage) : undefined;

	const dateLabel = date
		? (renderDateString(
				locale,
				date,
				placeholder,
				withTimeScale,
				intervalDisplayName,
				fiscalYearPrefix,
			) ?? noDateSelectedMessage)
		: noDateSelectedMessage;

	return dateLabel;
};
